/**
 * AdminAuditLog — support/compliance audit-trail slice.
 *
 * Route: /admin/audit-log (RBAC: platform_admin, compliance_admin, support_admin).
 * Reads the deliberately narrow /api/admin/audit-log endpoint which
 * returns rows from the shared audit_log collection filtered to the
 * age-gate actions (user.dob_corrected, age_gate_flag.updated,
 * age_gate.policy_drift_sweep). Compliance can free-text search actor
 * or target and confirm every DOB touch without shell access.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, RefreshCw, Search, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const ACTION_LABELS = {
    "user.dob_corrected": { label: "DOB corrected", tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
    "age_gate_flag.updated": { label: "Flag updated", tone: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400" },
    "age_gate.policy_drift_sweep": { label: "Policy sweep", tone: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
};

const ActionPill = ({ action }) => {
    const cfg = ACTION_LABELS[action] || { label: action, tone: "border-border bg-muted/40 text-muted-foreground" };
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cfg.tone}`}>{cfg.label}</span>;
};

export default function AdminAuditLog() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [pendingQ, setPendingQ] = useState("");
    const [selectedActions, setSelectedActions] = useState(["user.dob_corrected", "age_gate_flag.updated"]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { actions: selectedActions.join(",") };
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/audit-log", { params });
            setRows(data?.results || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load audit log.");
        } finally { setLoading(false); }
    }, [q, selectedActions]);

    useEffect(() => { load(); }, [load]);

    const toggleAction = (a) => {
        setSelectedActions((cur) => cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]);
    };

    return (
        <div className="container-app py-10" data-testid="admin-audit-log-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Compliance ops</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Audit log — age-gate slice
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        Read-only trail of every DOB correction, age-gate flag transition, and policy-drift sweep. Search
                        by admin email or target user ID to review a specific reviewer or account.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={load} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="audit-refresh">
                        <RefreshCw size={12} /> Refresh
                    </button>
                    <Link to="/admin/background-checks/age-flags" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="audit-back-link">
                        <ShieldCheck size={12} /> Back to age gate
                    </Link>
                </div>
            </header>

            <section className="mt-6 card-soft p-4" data-testid="audit-filters">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[280px]">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={pendingQ}
                            onChange={(e) => setPendingQ(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") setQ(pendingQ); }}
                            placeholder="Search actor email or target user_id…"
                            className="pl-9"
                            data-testid="audit-search-input"
                        />
                    </div>
                    <button
                        onClick={() => setQ(pendingQ)}
                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                        data-testid="audit-search-btn"
                    >
                        <Search size={12} /> Search
                    </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {Object.keys(ACTION_LABELS).map((a) => (
                        <button
                            key={a}
                            onClick={() => toggleAction(a)}
                            className={`rounded-full border px-2.5 py-1 text-xs ${
                                selectedActions.includes(a)
                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold"
                                    : "border-border text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`audit-filter-${a.replace(/\./g, "-")}`}
                        >
                            {ACTION_LABELS[a].label}
                        </button>
                    ))}
                </div>
            </section>

            <section className="mt-4">
                {loading ? (
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="card-soft p-6 text-center text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                        <ScrollText size={14} /> No entries match this filter.
                    </div>
                ) : (
                    <div className="card-soft p-0 overflow-hidden" data-testid="audit-list">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-muted-foreground border-b border-border bg-muted/30">
                                    <tr>
                                        <th className="text-left py-2 px-3">When</th>
                                        <th className="text-left py-2 px-3">Action</th>
                                        <th className="text-left py-2 px-3">Actor</th>
                                        <th className="text-left py-2 px-3">Target</th>
                                        <th className="text-left py-2 px-3">Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={(r.id) || i} className="border-b border-border last:border-b-0" data-testid={`audit-row-${i}`}>
                                            <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{fmt(r.at)}</td>
                                            <td className="py-2 px-3"><ActionPill action={r.action} /></td>
                                            <td className="py-2 px-3 font-mono text-[11px] text-foreground">{r.actor || "—"}</td>
                                            <td className="py-2 px-3 font-mono text-[11px] text-muted-foreground">{r.target || "—"}</td>
                                            <td className="py-2 px-3 text-[11px] text-muted-foreground max-w-md">
                                                {r.meta ? (
                                                    <details>
                                                        <summary className="cursor-pointer hover:text-foreground">view</summary>
                                                        <pre className="mt-1 whitespace-pre-wrap break-words text-[10px] rounded bg-muted/30 p-2">{JSON.stringify(r.meta, null, 2)}</pre>
                                                    </details>
                                                ) : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
